import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { nameConverter } from '../../global/render-api';
import { Attributes } from '../ui-element.type';
import styles from './ui-datalist.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIDatalist
 * @element ui-datalist
 * @classdesc Represents a class for <code>ui-datalist</code> element.
 * Helpers element, which holds data as list. Used mostly with custom selects.
 * @slot
 * @example
 * <ui-datalist>
 *   <option value="name1">
 *   <option value="name2">
 *   <option value="family1">
 *   <option value="family2">
 * </ui-datalist>
 */
@Component({ tag: 'ui-datalist', styles })
export class UIDatalist extends UIElement {
    /**
     * Returns all options.
     * @returns {Array<HTMLOptionElement>}
     */
    getOptions(): Array<HTMLOptionElement> {
        return [
            ...this.querySelectorAll<HTMLOptionElement>('option,ui-option'),
        ];
    }

    /**
     * Returns attributes of given option.
     * @param {HTMLOptionElement} option Option inside datalist.
     * @param {boolean} [keepDatasetNames] Keeps dataset attribute names and
     * don't convert them in camel case.
     * @returns {Record<string, string>}
     */
    getOptionAttributes(
        option: HTMLOptionElement,
        keepDatasetNames = false
    ): Attributes<string> {
        const attributes: Attributes<string> = {};
        if (keepDatasetNames) {
            Array.from(option.attributes).forEach((attr) => {
                const name = attr.name.replace(/^data-/, '');
                attributes[name] = attr.value;
            });
        } else {
            for (const key in option.dataset) {
                // skip data-key attribute for purpose.
                // keep data-wt-label attr as is for analytics.
                if (option.dataset.hasOwnProperty(key) && key !== 'key') {
                    const fullAttrName = `data-${nameConverter.propToAttribute(key)}`;
                    if (fullAttrName === 'data-wt-label') {
                        attributes[fullAttrName] = option.dataset[key]!;
                    } else {
                        attributes[key] = option.dataset[key]!;
                    }
                }
            }
        }
        if (option.hasAttribute('value')) {
            attributes.value = option.value;
        }
        return attributes;
    }

    /**
     * @inheritDoc
     */
    override render(): void {}
}
